"use client";

/**
 * The feedback offer on every page, not only the hub (owner,
 * 2026-10-15; the rules are spelled out in lib/feedback-prompt). The
 * first time it comes up in a day it dims the page behind a card with
 * the invitation e-mail's illustration; after that it is the same card
 * in the bottom corner. Both are white cards with one indigo button (the
 * quiet UI, 2026-10-29), both come up a moment
 * after the page loads, and "Sonra" is inert for the first seconds, then
 * lets this page through only. It is an offer of the interruption budget
 * (lib/floating-slot): it waits its turn, never comes up on a session's
 * first page view or over a result or a drawer, and comes up at most 6
 * times per account (the API counts them). It goes for good when the
 * account has answered or the offer has ended.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/tracking";
import { useLocale, useT } from "@/lib/locale-context";
import {
  CLOSE_AFTER_MS, OVERLAY_SEEN_AT_KEY, OVERLAY_SEEN_KEY, REST_AT_KEY, SHOW_AFTER_MS, canClosePrompt, capReached, laterCountdown, resting,
  overlaySeenRecently, promptMode, type PromptMode,
} from "@/lib/feedback-prompt";
import { useInterruption } from "@/lib/floating-slot";
import {
  FeedbackOfferForm, OFFER_CHANGED_EVENT, dateLabel, fill, loadOffer, reportPromptShown, type OfferState,
} from "@/components/FeedbackOffer";
import { FLOAT_CARD, TAG } from "@/lib/ui-classes";

// A floating card on white with the hairline border (quiet UI, 2026-10-29:
// no brand-filled cards); only the main button is indigo, the tag is grey.
const CARD = FLOAT_CARD;
const CTA = "rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";
const LATER = "text-sm font-semibold text-gray-600 underline-offset-4 transition-colors hover:text-gray-900 hover:underline focus-visible:outline-none focus-visible:underline disabled:cursor-default disabled:opacity-35 disabled:hover:no-underline";
/** The invitation e-mail's hero (public/email/feedback-hero.png, 1200x780). */
const HERO = "/email/feedback-hero.png";

function readOverlaySeen(): boolean {
  // Once a day per browser (localStorage); the per-tab flag stays as the
  // fallback. Storage that throws counts as seen: never lock a page we
  // cannot remember having locked.
  try {
    const at = localStorage.getItem(OVERLAY_SEEN_AT_KEY);
    if (at !== null && overlaySeenRecently(Number(at), Date.now())) return true;
  } catch { /* fall through to the per-tab flag */ }
  try { return sessionStorage.getItem(OVERLAY_SEEN_KEY) === "1"; } catch { return true; }
}

function readRestAt(): number | null {
  try {
    const at = localStorage.getItem(REST_AT_KEY);
    return at === null ? null : Number(at);
  } catch { return null; }
}

function markRest(): void {
  try { localStorage.setItem(REST_AT_KEY, String(Date.now())); } catch { /* this page only */ }
}

function markOverlaySeen(): void {
  try { localStorage.setItem(OVERLAY_SEEN_AT_KEY, String(Date.now())); } catch { /* per-tab only */ }
  try { sessionStorage.setItem(OVERLAY_SEEN_KEY, "1"); } catch { /* this page only */ }
}

export default function FeedbackOfferPrompt() {
  const t = useT();
  const { locale } = useLocale();
  const pathname = usePathname();
  const [offer, setOffer] = useState<OfferState | null>(null);
  const [overlaySeen, setOverlaySeen] = useState(true);
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);
  // Read when a page opens, not live: the stamp this page writes when
  // its card comes up rests the NEXT pages, not this one.
  const [restAt, setRestAt] = useState<number | null>(null);
  // Kept by page, so a page just opened never reads the last page's
  // state: the page whose moment-after-load is over, and the page the
  // card came up on (counted once there; that count caps the NEXT pages).
  const [armedFor, setArmedFor] = useState<string | null>(null);
  const [upOn, setUpOn] = useState<string | null>(null);
  const [closable, setClosable] = useState(false);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const sent = useRef(false);

  useEffect(() => { setMounted(true); setOverlaySeen(readOverlaySeen()); }, []);
  // Per page: an overlay shown on the previous page (even if left without
  // "later") makes this one a corner card, and its stamp rests this one.
  useEffect(() => { setRestAt(readRestAt()); setOverlaySeen(readOverlaySeen()); setUpOn(null); }, [pathname]);

  // The offer for this account, re-read when a form elsewhere on the
  // page (the hub card, the e-mail link) has just sent an answer.
  useEffect(() => {
    let alive = true;
    const read = () => loadOffer().then((o) => { if (alive) setOffer(o); }).catch(() => {});
    read();
    window.addEventListener(OFFER_CHANGED_EVENT, read);
    return () => { alive = false; window.removeEventListener(OFFER_CHANGED_EVENT, read); };
  }, []);

  const mode: PromptMode = mounted
    ? promptMode({
      state: offer?.state, overlaySeen, dismissedPath, path: pathname,
      resting: resting(restAt, Date.now()), capped: capReached(offer?.prompt_left, upOn === pathname),
    })
    : "none";
  const due = mode !== "none";

  // Come up a moment after each page settles.
  useEffect(() => {
    if (!due) { setArmedFor(null); return undefined; }
    const id = window.setTimeout(() => setArmedFor(pathname), SHOW_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [due, pathname]);
  const armed = armedFor === pathname;

  // The card waits for its turn (lib/floating-slot); the form opened from
  // it keeps the turn until it closes, so nothing comes up over it.
  const turn = useInterruption("feedback", due && (armed || open));
  const up = turn && armed && !open;

  // Once per page, the moment the card is on screen: the overlay is spent
  // for the day (a reload or the next page gets the corner card), the
  // next pages rest, and the show counts (the event and the account's cap).
  useEffect(() => {
    if (!up || upOn === pathname) return;
    setUpOn(pathname);
    if (mode === "overlay") markOverlaySeen();
    markRest();
    trackEvent("fbo.prompt", { mode, trial: Boolean(offer?.trial_active), path: pathname });
    void reportPromptShown().then((left) => {
      if (left !== null) setOffer((o) => (o ? { ...o, prompt_left: left } : o));
    });
  }, [up, upOn, pathname, mode, offer?.trial_active]);

  // "Later" is offered only once the card has been on screen a while.
  useEffect(() => {
    setClosable(false);
    if (!up) return undefined;
    const id = window.setTimeout(() => setClosable(canClosePrompt(CLOSE_AFTER_MS)), CLOSE_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [up]);

  // The seconds until "later" wakes up, shown on the control itself.
  const [countdown, setCountdown] = useState(0);
  useEffect(() => {
    if (!up || closable) { setCountdown(0); return undefined; }
    const started = Date.now();
    setCountdown(laterCountdown(0));
    const id = window.setInterval(() => setCountdown(laterCountdown(Date.now() - started)), 250);
    return () => window.clearInterval(id);
  }, [up, closable]);

  // Under the overlay or the form the page behind does not scroll.
  const lock = (up && mode === "overlay") || open;
  useEffect(() => {
    if (!lock) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [lock]);

  // The form stays until the person closes it: an answer sent re-reads
  // the offer as "submitted", and the thank-you must not vanish with it.
  if (!offer || (!due && !open)) return null;
  const trial = offer.trial_active;
  const vars = { days: offer.bonus_days, date: dateLabel(offer.until, locale) };
  // The same line the invitation e-mail carried; trial and Pro accounts
  // keep the hub's "extend" titles.
  const title = offer.pro_active ? t("fbo.title.pro") : trial ? t("fbo.title.trial") : fill(t("fbo.prompt.title"), vars);
  const text = fill(t(offer.pro_active ? "fbo.prompt.text.pro" : trial ? "fbo.prompt.text.trial" : "fbo.prompt.text"), vars);

  const later = () => {
    trackEvent("fbo.prompt.later", { mode, path: pathname });
    // The overlay is spent for this session either way; the corner card
    // follows on the next page.
    setOverlaySeen(true);
    setDismissedPath(pathname);
    markRest();
  };
  const openForm = () => { trackEvent("fbo.prompt.open", { mode, trial }); setOpen(true); };

  const body = (big: boolean) => (
    <>
      <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-bold tabular-nums ${TAG}`}>
        {fill(t("fbo.prompt.pill"), vars)}
      </span>
      <p className={`${big ? "mt-3 text-2xl" : "mt-2.5 text-xl"} font-bold leading-tight tracking-tight`}>{title}</p>
      <p className={`${big ? "mt-2 text-[15px]" : "mt-1.5 text-sm"} leading-relaxed text-gray-600`}>{text}</p>
      <div className={`${big ? "mt-5" : "mt-4"} flex items-center gap-4`}>
        <button type="button" onClick={openForm} className={CTA}>{t("fbo.cta")}</button>
        {/* "Later" is offered only once the card has been read; before that the control is there but inert. */}
        <button type="button" onClick={closable ? later : undefined} disabled={!closable} aria-disabled={!closable} className={LATER}>
          {t("fbo.later")}{!closable && countdown > 0 ? ` · ${countdown}` : ""}
        </button>
      </div>
    </>
  );

  return (
    <>
      {up && mode === "overlay" && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="fixed inset-0 z-[1290] flex items-end justify-center bg-black/40 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] modal-backdrop-enter sm:items-center"
        >
          <section className={`modal-enter w-full max-w-md overflow-hidden rounded-2xl ${CARD}`}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={HERO} alt="" width={1200} height={780} className="block h-40 w-full object-cover sm:h-52" />
            <div className="p-6">{body(true)}</div>
          </section>
        </div>,
        document.body,
      )}
      {up && mode === "corner" && (
        <section
          aria-label={title}
          className={`modal-enter fixed inset-x-4 bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] z-[1096] rounded-2xl p-5 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[400px] ${CARD}`}
        >
          {body(false)}
        </section>
      )}
      {open && (
        <FeedbackOfferForm
          offer={offer}
          onClose={() => {
            setOpen(false);
            if (sent.current) setOffer({ ...offer, state: "submitted" });
          }}
          onSent={() => { sent.current = true; }}
        />
      )}
    </>
  );
}
